import {
  approveCurrentArea,
  buildExpertZip,
  computeExpertPreview,
  downloadBlob,
  expertSession,
  regenerateCurrentArea,
  regenerateRejectedAndErrors,
  resetExpertSession,
  setResultStatus,
  startExpertBatch,
  stopExpertBatch,
  subscribeExpert,
  type ExpertResult,
  type ExpertSession,
  type ExpertStatus,
} from '../expert';
import { areaDims } from '../lib/area-geometry';
import { escapeHtml } from '../lib/escape';
import { saveAltShortLabel, saveOptionShortLabel } from '../lib/short-label-store';
import { slugifyAltShortLabel } from '../lib/slug';
import { ensureExpertConfigShape, slugifyShortLabel, state } from '../state';
import type {
  Area,
  ChoiceQuestion,
  ExpertMode,
  ExpertQuestionConfig,
  Question,
} from '../types';

type View = 'config' | 'review';

interface ModalRefs {
  overlay: HTMLElement;
  closeBtn: HTMLButtonElement;
  titleSubEl: HTMLElement;
  // config view
  configView: HTMLElement;
  configList: HTMLElement;
  modeSelect: HTMLSelectElement;
  altModeSelect: HTMLSelectElement;
  iterSlider: HTMLInputElement;
  iterValue: HTMLElement;
  concSlider: HTMLInputElement;
  concValue: HTMLElement;
  summary: HTMLElement;
  issuesEl: HTMLElement;
  generateBtn: HTMLButtonElement;
  autoApproveInput: HTMLInputElement;
  // review view
  reviewView: HTMLElement;
  reviewBack: HTMLButtonElement;
  reviewStats: HTMLElement;
  thumbStrip: HTMLElement;
  stage: HTMLElement;
  meta: HTMLElement;
  approveBtn: HTMLButtonElement;
  rejectBtn: HTMLButtonElement;
  unreviewBtn: HTMLButtonElement;
  regenBtn: HTMLButtonElement;
  downloadBtn: HTMLButtonElement;
  includeUnreviewedToggle: HTMLInputElement;
  stopBtn: HTMLButtonElement;
  outlineToggleBtn: HTMLButtonElement;
  baseToggleBtn: HTMLButtonElement;
}

let refs: ModalRefs | null = null;
let view: View = 'config';
let selectedResultId: string | null = null;
let unsubscribe: (() => void) | null = null;
let keyHandler: ((e: KeyboardEvent) => void) | null = null;
let outlineHidden = false;
let showingBase = false;
let stageResizeObs: ResizeObserver | null = null;
let stageWindowListener: (() => void) | null = null;
const expandedAltOpts = new Set<string>();

export function openExpertModal(): void {
  ensureExpertConfigShape();
  ensureModal();
  if (!refs) return;
  refs.overlay.classList.remove('hidden');
  if (expertSession.results.length > 0) {
    setView('review');
  } else {
    setView('config');
  }
  if (!unsubscribe) unsubscribe = subscribeExpert(render);
  if (!keyHandler) {
    keyHandler = (e) => onKeyDown(e);
    document.addEventListener('keydown', keyHandler);
  }
  render();
}

function closeModal(): void {
  if (!refs) return;
  refs.overlay.classList.add('hidden');
  if (unsubscribe) {
    unsubscribe();
    unsubscribe = null;
  }
  if (keyHandler) {
    document.removeEventListener('keydown', keyHandler);
    keyHandler = null;
  }
  teardownStageObservers();
}

function ensureModal(): void {
  if (refs) return;
  const overlay = document.createElement('div');
  overlay.className = 'expert-modal hidden';
  overlay.innerHTML = `
    <div class="expert-dialog">
      <div class="expert-dialog-header">
        <div class="expert-dialog-titles">
          <span class="expert-dialog-title">Expert Mode</span>
          <span class="expert-dialog-sub"></span>
        </div>
        <button type="button" class="expert-close-btn" aria-label="Close">×</button>
      </div>

      <div class="expert-view expert-view-config">
        <div class="expert-config-list"></div>

        <div class="expert-config-footer">
          <div class="expert-slider-row">
            <label class="expert-slider">
              <span class="expert-slider-label">Mode</span>
              <select class="expert-mode-select">
                <option value="permute">Permute — every combination</option>
                <option value="cover">Cover — each option at least once</option>
              </select>
            </label>
            <label class="expert-slider">
              <span class="expert-slider-label">Phrasings</span>
              <select class="expert-alt-mode-select">
                <option value="random">Random per iteration</option>
                <option value="simple">Simple — cycle within each row</option>
                <option value="smart">Smart — minimal covering set</option>
              </select>
            </label>
            <label class="expert-slider">
              <span class="expert-slider-label">Iterations per permutation</span>
              <input type="range" class="expert-iter-slider" min="1" max="20" value="1" />
              <span class="expert-slider-value expert-iter-value">1</span>
            </label>
            <label class="expert-slider">
              <span class="expert-slider-label">Concurrency</span>
              <input type="range" class="expert-conc-slider" min="1" max="50" value="3" />
              <span class="expert-slider-value expert-conc-value">3</span>
            </label>
          </div>
          <label class="expert-auto-approve">
            <input type="checkbox" class="expert-auto-approve-input" />
            <span>Review each area as it generates (pauses after each area for approval or regenerate)</span>
          </label>
          <div class="expert-summary"></div>
          <div class="expert-issues hidden"></div>
          <button type="button" class="expert-generate-btn">Generate</button>
        </div>
      </div>

      <div class="expert-view expert-view-review hidden">
        <div class="expert-review-topbar">
          <button type="button" class="expert-review-back">← Config</button>
          <span class="expert-review-stats"></span>
          <div class="expert-review-actions">
            <label class="expert-include-unreviewed">
              <input type="checkbox" class="expert-include-unreviewed-input" />
              <span>Include unreviewed</span>
            </label>
            <button type="button" class="expert-stop-btn hidden">Stop</button>
            <button type="button" class="expert-regen-btn">Regenerate rejected</button>
            <button type="button" class="expert-download-btn">Download zip</button>
          </div>
        </div>
        <div class="expert-review-body">
          <div class="expert-thumb-strip"></div>
          <div class="expert-resizer" role="separator" aria-orientation="vertical" title="Drag to resize"></div>
          <div class="expert-viewer">
            <div class="expert-stage">
              <div class="expert-stage-toolbar">
                <button type="button" class="expert-outline-toggle" title="Toggle area outline (O)">Outline (O)</button>
                <button type="button" class="expert-base-toggle" title="Toggle base artwork (B)">Base (B)</button>
              </div>
            </div>
            <div class="expert-meta"></div>
            <div class="expert-review-controls">
              <button type="button" class="expert-reject-btn">Reject (R)</button>
              <button type="button" class="expert-unreview-btn">Un-review (U)</button>
              <button type="button" class="expert-approve-btn">Approve (A)</button>
            </div>
            <div class="expert-keyhint">A/Space approve · R/⌫ reject · ←/→ navigate · U un-review · O outline · B base · Esc close</div>
          </div>
        </div>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  refs = {
    overlay,
    closeBtn: overlay.querySelector<HTMLButtonElement>('.expert-close-btn')!,
    titleSubEl: overlay.querySelector<HTMLElement>('.expert-dialog-sub')!,
    configView: overlay.querySelector<HTMLElement>('.expert-view-config')!,
    configList: overlay.querySelector<HTMLElement>('.expert-config-list')!,
    modeSelect: overlay.querySelector<HTMLSelectElement>('.expert-mode-select')!,
    altModeSelect: overlay.querySelector<HTMLSelectElement>('.expert-alt-mode-select')!,
    iterSlider: overlay.querySelector<HTMLInputElement>('.expert-iter-slider')!,
    iterValue: overlay.querySelector<HTMLElement>('.expert-iter-value')!,
    concSlider: overlay.querySelector<HTMLInputElement>('.expert-conc-slider')!,
    concValue: overlay.querySelector<HTMLElement>('.expert-conc-value')!,
    summary: overlay.querySelector<HTMLElement>('.expert-summary')!,
    issuesEl: overlay.querySelector<HTMLElement>('.expert-issues')!,
    generateBtn: overlay.querySelector<HTMLButtonElement>('.expert-generate-btn')!,
    autoApproveInput: overlay.querySelector<HTMLInputElement>('.expert-auto-approve-input')!,
    reviewView: overlay.querySelector<HTMLElement>('.expert-view-review')!,
    reviewBack: overlay.querySelector<HTMLButtonElement>('.expert-review-back')!,
    reviewStats: overlay.querySelector<HTMLElement>('.expert-review-stats')!,
    thumbStrip: overlay.querySelector<HTMLElement>('.expert-thumb-strip')!,
    stage: overlay.querySelector<HTMLElement>('.expert-stage')!,
    meta: overlay.querySelector<HTMLElement>('.expert-meta')!,
    approveBtn: overlay.querySelector<HTMLButtonElement>('.expert-approve-btn')!,
    rejectBtn: overlay.querySelector<HTMLButtonElement>('.expert-reject-btn')!,
    unreviewBtn: overlay.querySelector<HTMLButtonElement>('.expert-unreview-btn')!,
    regenBtn: overlay.querySelector<HTMLButtonElement>('.expert-regen-btn')!,
    downloadBtn: overlay.querySelector<HTMLButtonElement>('.expert-download-btn')!,
    includeUnreviewedToggle: overlay.querySelector<HTMLInputElement>(
      '.expert-include-unreviewed-input',
    )!,
    stopBtn: overlay.querySelector<HTMLButtonElement>('.expert-stop-btn')!,
    outlineToggleBtn: overlay.querySelector<HTMLButtonElement>('.expert-outline-toggle')!,
    baseToggleBtn: overlay.querySelector<HTMLButtonElement>('.expert-base-toggle')!,
  };

  wire();
}

function wireResizer(): void {
  if (!refs) return;
  const resizer = refs.overlay.querySelector<HTMLElement>('.expert-resizer');
  const body = refs.overlay.querySelector<HTMLElement>('.expert-review-body');
  const strip = refs.thumbStrip;
  if (!resizer || !body) return;

  let dragging = false;
  let startX = 0;
  let startWidth = 0;

  const onMove = (e: PointerEvent) => {
    if (!dragging) return;
    const bodyRect = body.getBoundingClientRect();
    const next = Math.min(
      Math.max(startWidth + (e.clientX - startX), 180),
      Math.max(240, bodyRect.width - 320),
    );
    strip.style.width = `${next}px`;
    e.preventDefault();
  };

  const onUp = () => {
    if (!dragging) return;
    dragging = false;
    document.body.classList.remove('expert-resizing');
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp);
  };

  resizer.addEventListener('pointerdown', (e) => {
    dragging = true;
    startX = e.clientX;
    startWidth = strip.getBoundingClientRect().width;
    document.body.classList.add('expert-resizing');
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    e.preventDefault();
  });
}

function wire(): void {
  if (!refs) return;
  refs.closeBtn.addEventListener('click', () => closeModal());
  refs.overlay.addEventListener('click', (e) => {
    if (e.target === refs!.overlay) closeModal();
  });

  wireResizer();

  refs.modeSelect.addEventListener('change', () => {
    const v = refs!.modeSelect.value;
    state.expertPermutationMode = v === 'cover' ? 'cover' : 'permute';
    renderSummary();
  });
  refs.altModeSelect.addEventListener('change', () => {
    const v = refs!.altModeSelect.value;
    state.expertAltCoverageMode =
      v === 'simple' ? 'simple' : v === 'smart' ? 'smart' : 'random';
    renderSummary();
  });
  refs.iterSlider.addEventListener('input', () => {
    state.expertIterationsPerPermutation = Number(refs!.iterSlider.value);
    refs!.iterValue.textContent = refs!.iterSlider.value;
    renderSummary();
  });
  refs.concSlider.addEventListener('input', () => {
    state.expertConcurrency = Number(refs!.concSlider.value);
    refs!.concValue.textContent = refs!.concSlider.value;
  });
  refs.autoApproveInput.addEventListener('change', () => {
    state.expertReviewPerArea = refs!.autoApproveInput.checked;
  });

  refs.configList.addEventListener('click', onConfigClick);
  refs.configList.addEventListener('input', onConfigInput);

  refs.generateBtn.addEventListener('click', () => {
    void handleGenerate();
  });

  refs.reviewBack.addEventListener('click', () => {
    if (expertSession.running) {
      if (!confirm('A batch is still running. Go back to config anyway?')) return;
      stopExpertBatch();
    }
    setView('config');
    render();
  });

  refs.approveBtn.addEventListener('click', () => {
    if (!selectedResultId) return;
    if (isAwaitingArea(selectedResultId)) {
      approveCurrentArea(selectedResultId);
      jumpToNextAwaiting(selectedResultId);
    } else {
      setResultStatus(selectedResultId, 'approved');
      advance(1, true);
    }
  });
  refs.rejectBtn.addEventListener('click', () => {
    if (!selectedResultId) return;
    if (isAwaitingArea(selectedResultId)) {
      regenerateCurrentArea(selectedResultId);
      jumpToNextAwaiting(selectedResultId);
    } else {
      setResultStatus(selectedResultId, 'rejected');
      advance(1, true);
    }
  });
  refs.unreviewBtn.addEventListener('click', () => {
    if (selectedResultId) {
      setResultStatus(selectedResultId, 'pending');
      render();
    }
  });

  refs.regenBtn.addEventListener('click', () => {
    void regenerateRejectedAndErrors();
  });
  refs.downloadBtn.addEventListener('click', () => {
    const built = buildExpertZip({ includeUnreviewed: refs!.includeUnreviewedToggle.checked });
    if (built.included === 0) {
      alert('Nothing to download — no approved images.');
      return;
    }
    downloadBlob(built.blob, built.filename);
  });
  refs.stopBtn.addEventListener('click', () => stopExpertBatch());

  refs.outlineToggleBtn.addEventListener('click', () => {
    outlineHidden = !outlineHidden;
    renderViewer();
  });
  refs.baseToggleBtn.addEventListener('click', () => {
    showingBase = !showingBase;
    renderViewer();
  });

  refs.thumbStrip.addEventListener('click', (e) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>('.expert-thumb');
    if (!el) return;
    const id = el.dataset.id;
    if (id) {
      selectedResultId = id;
      render();
    }
  });
}

function setView(next: View): void {
  view = next;
  if (!refs) return;
  refs.configView.classList.toggle('hidden', next !== 'config');
  refs.reviewView.classList.toggle('hidden', next !== 'review');
}

function render(): void {
  if (!refs) return;
  if (view === 'config') renderConfig();
  else renderReview();
}

function renderConfig(): void {
  if (!refs) return;
  refs.modeSelect.value = state.expertPermutationMode;
  refs.altModeSelect.value = state.expertAltCoverageMode;
  const altOn = state.expertAltCoverageMode !== 'random';
  refs.iterSlider.disabled = altOn;
  refs.modeSelect.disabled = state.expertAltCoverageMode === 'smart';
  refs.iterSlider.value = String(state.expertIterationsPerPermutation);
  refs.iterValue.textContent = String(state.expertIterationsPerPermutation);
  refs.autoApproveInput.checked = state.expertReviewPerArea;
  refs.concSlider.value = String(state.expertConcurrency);
  refs.concValue.textContent = String(state.expertConcurrency);

  refs.configList.innerHTML = state.questions
    .map((q) => renderQuestionRow(q))
    .join('');

  renderSummary();
}

function renderSummary(): void {
  if (!refs) return;
  const altOn = state.expertAltCoverageMode !== 'random';
  refs.iterSlider.disabled = altOn;
  refs.modeSelect.disabled = state.expertAltCoverageMode === 'smart';

  const preview = computeExpertPreview();
  const hasPermute = preview.permuteQids.length > 0;
  const isCover = state.expertPermutationMode === 'cover';
  const altMode = state.expertAltCoverageMode;
  const poolsDesc = preview.poolSizes.length
    ? preview.poolSizes.join(isCover || altMode !== 'random' ? ', ' : ' × ')
    : '—';

  let summary: string;
  if (!hasPermute) {
    summary = `<strong>${preview.totalIterations}</strong> iterations (no permute questions)`;
  } else if (altMode === 'smart') {
    summary = `smart coverage over pools (${poolsDesc}) = <strong>${preview.totalIterations}</strong> iterations`;
  } else if (altMode === 'simple') {
    const baseLabel = isCover ? 'cover rows' : 'permutations';
    summary = `${poolsDesc} ${isCover ? '→' : '='} ${preview.permutationCount} ${baseLabel} × phrasing depth = <strong>${preview.totalIterations}</strong> iterations`;
  } else if (isCover) {
    summary = `max(${poolsDesc}) = <strong>${preview.permutationCount}</strong> iterations × ${state.expertIterationsPerPermutation} = <strong>${preview.totalIterations}</strong> iterations`;
  } else {
    summary = `${poolsDesc} = <strong>${preview.permutationCount}</strong> permutations × ${state.expertIterationsPerPermutation} = <strong>${preview.totalIterations}</strong> iterations`;
  }
  refs.summary.innerHTML = summary;

  if (preview.issues.length > 0) {
    refs.issuesEl.classList.remove('hidden');
    refs.issuesEl.innerHTML = `<ul>${preview.issues
      .map((i) => `<li>${escapeHtml(i)}</li>`)
      .join('')}</ul>`;
    refs.generateBtn.disabled = true;
  } else {
    refs.issuesEl.classList.add('hidden');
    refs.issuesEl.innerHTML = '';
    refs.generateBtn.disabled = false;
  }
}

function renderQuestionRow(q: Question): string {
  const cfg = state.expertConfig.get(q.id);
  if (!cfg) return '';
  const modeControl = renderModeControl(cfg.mode, q.kind);
  if (q.kind === 'choice') {
    const multiAltIdxs = q.options
      .map((opt, i) => (opt.injects && opt.prompts.some((g) => g.alternatives.length > 1) ? i : -1))
      .filter((i) => i >= 0);
    const allOpen =
      multiAltIdxs.length > 0 && multiAltIdxs.every((i) => expandedAltOpts.has(`${q.id}:${i}`));
    const headerCaret = multiAltIdxs.length > 0
      ? `<button type="button" class="expert-q-alt-toggle${allOpen ? ' open' : ''}" data-action="toggle-alts-all" aria-label="Expand all phrasings" title="Expand all phrasings">▸</button>`
      : '';
    return `
      <div class="expert-q-row" data-qid="${escapeHtml(q.id)}" data-kind="choice" data-color="${q.colorIndex}">
        <div class="expert-q-head">
          ${headerCaret}
          <span class="expert-q-title">${escapeHtml(q.title)}</span>
          ${modeControl}
        </div>
        <div class="expert-q-pool">
          ${renderOptionChips(q, cfg)}
        </div>
      </div>
    `;
  }
  const photoModeControl = `
    <div class="expert-seg expert-photo-mode" data-photo-mode>
      ${renderSegBtn('fixed', cfg.photoMode === 'fixed', 'Fixed')}
      ${renderSegBtn('random', cfg.photoMode === 'random', 'Random portrait')}
    </div>
  `;
  return `
    <div class="expert-q-row" data-qid="${escapeHtml(q.id)}" data-kind="photo" data-color="photo">
      <div class="expert-q-head">
        <span class="expert-q-title">${escapeHtml(q.title)}</span>
        ${photoModeControl}
      </div>
    </div>
  `;
}

function renderModeControl(mode: ExpertMode, kind: 'choice' | 'photo'): string {
  if (kind === 'photo') return '';
  return `
    <div class="expert-seg expert-mode" data-mode>
      ${renderSegBtn('fixed', mode === 'fixed', 'Fixed')}
      ${renderSegBtn('shuffle', mode === 'shuffle', 'Shuffle')}
      ${renderSegBtn('rotate', mode === 'rotate', 'Rotate')}
      ${renderSegBtn('permute', mode === 'permute', 'Permute')}
    </div>
  `;
}

function renderSegBtn(value: string, active: boolean, label: string): string {
  return `<button type="button" class="expert-seg-btn${active ? ' active' : ''}" data-value="${value}">${label}</button>`;
}

function renderOptionChips(q: ChoiceQuestion, cfg: ExpertQuestionConfig): string {
  return q.options
    .map((opt, i) => {
      const o = cfg.options[i];
      if (!o) return '';
      const selectedForFixed = cfg.mode === 'fixed' && o.inPool;
      const hasAlts = opt.injects && opt.prompts.some((g) => g.alternatives.length > 1);
      const expandKey = `${q.id}:${i}`;
      const expanded = expandedAltOpts.has(expandKey);
      const caret = hasAlts
        ? `<button type="button" class="expert-opt-alt-caret${expanded ? ' open' : ''}" data-action="toggle-alts" aria-label="Toggle phrasing labels" title="Toggle phrasing labels">▸</button>`
        : '';
      return `
        <div class="expert-opt${o.inPool ? ' in-pool' : ''}${selectedForFixed ? ' fixed-selected' : ''}${expanded ? ' alts-expanded' : ''}" data-opt-idx="${i}">
          <div class="expert-opt-head">
            <button type="button" class="expert-opt-toggle" data-action="toggle-pool" aria-label="Toggle in pool">
              ${escapeHtml(opt.label)}
            </button>
            <input
              class="expert-opt-short"
              type="text"
              value="${escapeHtml(o.shortLabel)}"
              placeholder="${escapeHtml(slugifyShortLabel(opt.label))}"
              data-action="short"
              maxlength="24"
              title="Short label used in filenames"
            />
            ${caret}
          </div>
          ${renderOptionAltLabels(opt, i, cfg.mode === 'fixed' && o.inPool, o.fixedAltByPrompt, q.id)}
        </div>
      `;
    })
    .join('');
}

function renderOptionAltLabels(
  opt: ChoiceQuestion['options'][number],
  optIdx: number,
  showRadio: boolean,
  fixedAltByPrompt: Record<number, number> | undefined,
  qid: string,
): string {
  if (!opt.injects || opt.prompts.length === 0) return '';
  const multiGroups = opt.prompts
    .map((g, idx) => ({ g, idx }))
    .filter(({ g }) => g.alternatives.length > 1);
  if (multiGroups.length === 0) return '';
  const groupsHtml = multiGroups
    .map(({ g, idx }) => {
      const groupPinned = fixedAltByPrompt?.[idx];
      const radioName = `fixed-alt-${qid}-${optIdx}-${idx}`;
      const rows = g.alternatives
        .map((alt, ai) => {
          const preview = alt.text.length > 48 ? `${alt.text.slice(0, 48)}…` : alt.text;
          const radio = showRadio
            ? `<input type="radio" class="expert-opt-alt-pick" name="${escapeHtml(radioName)}" data-action="alt-pick" ${groupPinned === ai ? 'checked' : ''} title="Use this alt in Fixed mode" />`
            : '';
          const picked = showRadio && groupPinned === ai;
          return `
            <div class="expert-opt-alt${picked ? ' picked' : ''}" data-alt-idx="${ai}" data-prompt-idx="${idx}">
              ${radio}
              <input
                class="expert-opt-alt-short"
                type="text"
                value="${escapeHtml(alt.shortLabel)}"
                placeholder="${escapeHtml(slugifyAltShortLabel(alt.text))}"
                data-action="alt-short"
                maxlength="16"
                title="Short label used in filenames"
              />
              <span class="expert-opt-alt-preview" title="${escapeHtml(alt.text)}">${escapeHtml(preview)}</span>
            </div>
          `;
        })
        .join('');
      const target = g.alternatives[0]?.target?.trim() ?? '';
      const header =
        multiGroups.length > 1
          ? `<div class="expert-opt-alt-group-header">${escapeHtml(target || `group ${idx + 1}`)}</div>`
          : '';
      return header + rows;
    })
    .join('');
  return `<div class="expert-opt-alts" data-opt-idx="${optIdx}">${groupsHtml}</div>`;
}

function onConfigClick(e: Event): void {
  const row = (e.target as HTMLElement).closest<HTMLElement>('.expert-q-row');
  if (!row) return;
  const qid = row.dataset.qid ?? '';
  const cfg = state.expertConfig.get(qid);
  const q = state.questions.find((x) => x.id === qid);
  if (!cfg || !q) return;

  const altToggleAll = (e.target as HTMLElement).closest<HTMLElement>('[data-action="toggle-alts-all"]');
  if (altToggleAll && q.kind === 'choice') {
    const multiAltIdxs = q.options
      .map((opt, i) => (opt.injects && opt.prompts.some((g) => g.alternatives.length > 1) ? i : -1))
      .filter((i) => i >= 0);
    const allOpen = multiAltIdxs.every((i) => expandedAltOpts.has(`${qid}:${i}`));
    if (allOpen) {
      for (const i of multiAltIdxs) expandedAltOpts.delete(`${qid}:${i}`);
    } else {
      for (const i of multiAltIdxs) expandedAltOpts.add(`${qid}:${i}`);
    }
    renderConfig();
    return;
  }

  const altToggle = (e.target as HTMLElement).closest<HTMLElement>('[data-action="toggle-alts"]');
  if (altToggle) {
    const optEl = altToggle.closest<HTMLElement>('.expert-opt');
    const idx = Number(optEl?.dataset.optIdx);
    if (!Number.isFinite(idx)) return;
    const key = `${qid}:${idx}`;
    if (expandedAltOpts.has(key)) expandedAltOpts.delete(key);
    else expandedAltOpts.add(key);
    renderConfig();
    return;
  }

  const seg = (e.target as HTMLElement).closest<HTMLElement>('.expert-seg-btn');
  if (seg) {
    const parent = seg.closest<HTMLElement>('[data-mode], [data-photo-mode]');
    if (!parent) return;
    const value = seg.dataset.value ?? '';
    if (parent.hasAttribute('data-mode')) {
      cfg.mode = value as ExpertMode;
      if (cfg.mode === 'fixed' && q.kind === 'choice') {
        const firstInPool = cfg.options.findIndex((o) => o.inPool);
        cfg.options.forEach((o, i) => {
          o.inPool = i === (firstInPool >= 0 ? firstInPool : 0);
        });
      }
    } else if (parent.hasAttribute('data-photo-mode')) {
      cfg.photoMode = value as 'fixed' | 'random';
    }
    renderConfig();
    return;
  }

  const altPick = (e.target as HTMLElement).closest<HTMLElement>('[data-action="alt-pick"]');
  if (altPick) {
    const optEl = altPick.closest<HTMLElement>('.expert-opt');
    const altEl = altPick.closest<HTMLElement>('.expert-opt-alt');
    const optIdx = Number(optEl?.dataset.optIdx);
    const altIdx = Number(altEl?.dataset.altIdx);
    const promptIdx = Number(altEl?.dataset.promptIdx);
    if (!Number.isFinite(optIdx) || !Number.isFinite(altIdx) || !Number.isFinite(promptIdx)) return;
    const o = cfg.options[optIdx];
    if (!o) return;
    if (!o.fixedAltByPrompt) o.fixedAltByPrompt = {};
    o.fixedAltByPrompt[promptIdx] = altIdx;
    renderConfig();
    return;
  }

  const toggle = (e.target as HTMLElement).closest<HTMLElement>('[data-action="toggle-pool"]');
  if (toggle) {
    const optEl = toggle.closest<HTMLElement>('.expert-opt');
    const idx = Number(optEl?.dataset.optIdx);
    if (!Number.isFinite(idx)) return;
    const o = cfg.options[idx];
    if (!o) return;
    if (cfg.mode === 'fixed') {
      cfg.options.forEach((x, i) => (x.inPool = i === idx));
    } else {
      o.inPool = !o.inPool;
    }
    renderConfig();
    return;
  }
}

function onConfigInput(e: Event): void {
  const row = (e.target as HTMLElement).closest<HTMLElement>('.expert-q-row');
  if (!row) return;
  const qid = row.dataset.qid ?? '';
  const cfg = state.expertConfig.get(qid);
  if (!cfg) return;

  const el = e.target as HTMLElement;
  if (el.matches('.expert-opt-short')) {
    const optEl = el.closest<HTMLElement>('.expert-opt');
    const idx = Number(optEl?.dataset.optIdx);
    if (!Number.isFinite(idx)) return;
    const o = cfg.options[idx];
    if (!o) return;
    const value = (el as HTMLInputElement).value;
    o.shortLabel = value;
    saveOptionShortLabel(qid, idx, value);
    return;
  }
  if (el.matches('.expert-opt-alt-short')) {
    const q = state.questions.find((x) => x.id === qid);
    if (!q || q.kind !== 'choice') return;
    const optEl = el.closest<HTMLElement>('.expert-opt');
    const altEl = el.closest<HTMLElement>('.expert-opt-alt');
    const optIdx = Number(optEl?.dataset.optIdx);
    const altIdx = Number(altEl?.dataset.altIdx);
    const promptIdx = Number(altEl?.dataset.promptIdx);
    if (!Number.isFinite(optIdx) || !Number.isFinite(altIdx) || !Number.isFinite(promptIdx)) return;
    const alt = q.options[optIdx]?.prompts[promptIdx]?.alternatives[altIdx];
    if (!alt) return;
    const value = (el as HTMLInputElement).value;
    alt.shortLabel = value;
    saveAltShortLabel(qid, optIdx, promptIdx, altIdx, value);
  }
}

async function handleGenerate(): Promise<void> {
  if (expertSession.running) return;
  if (expertSession.results.length > 0) {
    if (!confirm('Starting a new batch will discard the current review session. Continue?')) return;
    resetExpertSession();
  }
  selectedResultId = null;
  setView('review');
  render();
  try {
    await startExpertBatch();
  } catch (err) {
    alert((err as Error).message);
    setView('config');
    render();
    return;
  }
  // After batch completes, select first pending if none selected.
  if (!selectedResultId) {
    const first = expertSession.results.find((r) => r.status === 'pending');
    if (first) selectedResultId = first.id;
    render();
  }
}

function isAwaitingAreaResult(r: ExpertResult): boolean {
  return (
    r.status === 'running' &&
    r.currentAreaIdx !== null &&
    r.areaStatuses[r.currentAreaIdx] === 'awaiting'
  );
}

function isAwaitingArea(id: string): boolean {
  const r = expertSession.results.find((x) => x.id === id);
  return !!r && isAwaitingAreaResult(r);
}

function jumpToNextAwaiting(fromId: string): void {
  const results = expertSession.results;
  if (results.length === 0) return;
  const startIdx = results.findIndex((r) => r.id === fromId);
  const base = startIdx < 0 ? 0 : startIdx;
  for (let step = 1; step <= results.length; step++) {
    const r = results[(base + step) % results.length];
    if (r.id !== fromId && isAwaitingAreaResult(r)) {
      selectedResultId = r.id;
      render();
      return;
    }
  }
  // No other awaiting result. Leave selection as-is; render will reflect any status change.
  render();
}

function renderReview(): void {
  if (!refs) return;
  const results = expertSession.results;

  // Auto-select when nothing is selected: prefer an awaiting area, else any reviewable.
  if (!selectedResultId) {
    const awaiting = results.find(isAwaitingAreaResult);
    const first =
      awaiting ??
      results.find(
        (r) => r.status === 'pending' || r.status === 'error' || r.status === 'approved' || r.status === 'rejected',
      );
    if (first) selectedResultId = first.id;
  }

  // Stats
  const counts: Record<ExpertStatus, number> = {
    queued: 0, running: 0, pending: 0, approved: 0, rejected: 0, error: 0,
  };
  for (const r of results) counts[r.status]++;
  const total = results.length;
  const done = counts.pending + counts.approved + counts.rejected + counts.error;

  refs.reviewStats.innerHTML = `
    <span>${done} / ${total}</span>
    <span class="expert-stat expert-stat-approved">✓ ${counts.approved}</span>
    <span class="expert-stat expert-stat-rejected">✗ ${counts.rejected}</span>
    <span class="expert-stat expert-stat-pending">? ${counts.pending}</span>
    <span class="expert-stat expert-stat-error">! ${counts.error}</span>
    ${counts.running > 0 ? `<span class="expert-stat expert-stat-running">running ${counts.running}</span>` : ''}
    ${counts.queued > 0 ? `<span class="expert-stat expert-stat-queued">queued ${counts.queued}</span>` : ''}
  `;

  refs.stopBtn.classList.toggle('hidden', !expertSession.running);
  refs.regenBtn.disabled = (counts.rejected + counts.error) === 0;
  refs.downloadBtn.disabled = counts.approved === 0 && !(refs.includeUnreviewedToggle.checked && counts.pending > 0);

  // Thumbs
  refs.thumbStrip.innerHTML = results
    .map((r) => renderThumb(r, r.id === selectedResultId))
    .join('');

  // Auto-scroll selected thumb into view
  if (selectedResultId) {
    const sel = refs.thumbStrip.querySelector<HTMLElement>(`[data-id="${selectedResultId}"]`);
    if (sel) sel.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  // Viewer
  renderViewer();

  // Subtitle
  refs.titleSubEl.textContent = `${total} iterations · ${counts.approved} approved · ${counts.rejected} rejected`;
}

function renderThumb(r: ExpertResult, active: boolean): string {
  const statusClass = `status-${r.status}`;
  const fileHtml = `<span class="expert-row-file">${escapeHtml(r.fileName)}</span>`;
  const title = r.fileName;
  const awaiting = isAwaitingAreaResult(r);
  const showProgress =
    r.areasTotal > 0 &&
    (r.status === 'running' ||
      r.status === 'queued' ||
      (r.areasDone > 0 && r.areasDone < r.areasTotal));
  const progressHtml = awaiting
    ? `<span class="expert-row-progress awaiting">await ${((r.currentAreaIdx ?? 0) + 1)}/${r.areasTotal}</span>`
    : showProgress
      ? `<span class="expert-row-progress">${r.areasDone}/${r.areasTotal}</span>`
      : r.areasTotal > 0
        ? `<span class="expert-row-progress subtle">${r.areasTotal}/${r.areasTotal}</span>`
        : '';
  return `
    <div class="expert-thumb ${statusClass}${active ? ' active' : ''}" data-id="${escapeHtml(r.id)}" title="${escapeHtml(title)}">
      <span class="expert-thumb-pip"></span>
      <span class="expert-row-label">${fileHtml}</span>
      ${progressHtml}
    </div>
  `;
}

function renderViewer(): void {
  if (!refs) return;
  const r = expertSession.results.find((x) => x.id === selectedResultId);

  // Keep the stage-toolbar even when clearing the stage.
  const toolbarEl = refs.stage.querySelector<HTMLElement>('.expert-stage-toolbar');

  const clearFrame = (htmlContent: string): void => {
    refs!.stage.innerHTML = htmlContent;
    if (toolbarEl) refs!.stage.appendChild(toolbarEl);
    teardownStageObservers();
  };

  if (!r) {
    clearFrame(`<div class="expert-empty">No selection. Click a thumbnail or wait for results to arrive.</div>`);
    refs.meta.innerHTML = '';
    refs.approveBtn.disabled = true;
    refs.rejectBtn.disabled = true;
    refs.unreviewBtn.disabled = true;
    updateToggleButtons(false);
    return;
  }

  const snap = expertSession.snapshot;
  const bw = snap?.baseImage.width ?? 1;
  const bh = snap?.baseImage.height ?? 1;
  const baseUrl = snap?.baseImage.dataUrl ?? null;
  const canShowBase = showingBase && !!baseUrl;
  const displayUrl = canShowBase ? baseUrl : r.dataUrl ?? null;

  if (displayUrl) {
    let frame = refs.stage.querySelector<HTMLElement>('.expert-stage-frame');
    if (!frame || frame.dataset.resultId !== r.id) {
      // Preserve the toolbar while rebuilding the frame.
      refs.stage.innerHTML = `
        <div class="expert-stage-frame" data-result-id="${escapeHtml(r.id)}">
          <img src="${displayUrl}" alt="" class="expert-preview-img" />
          <div class="expert-area-box hidden">
            <div class="expert-area-spinner"><div class="gen-spinner"></div></div>
          </div>
        </div>
      `;
      if (toolbarEl) refs.stage.appendChild(toolbarEl);
      frame = refs.stage.querySelector<HTMLElement>('.expert-stage-frame');
      if (frame) setupStageObservers(frame);
    } else {
      const img = frame.querySelector<HTMLImageElement>('img.expert-preview-img');
      if (img && img.src !== displayUrl) img.src = displayUrl;
    }

    if (frame) {
      frame.classList.toggle('showing-base', canShowBase);
      const box = frame.querySelector<HTMLElement>('.expert-area-box');
      const areaForBox = pickAreaForOutline(r, snap);
      if (box) {
        if (areaForBox && snap) {
          positionAreaBox(frame, box, areaForBox, bw, bh);
          box.classList.remove('hidden');
          const generating = r.status === 'running' && !isAwaitingAreaResult(r);
          box.classList.toggle('generating', generating);
          box.classList.toggle('outline-off', outlineHidden);
        } else {
          box.classList.add('hidden');
          box.classList.remove('generating');
          box.classList.remove('outline-off');
        }
      }
    }
  } else if (r.status === 'error') {
    clearFrame(`<div class="expert-error-block">${escapeHtml(r.errorMessage ?? 'Error')}</div>`);
  } else {
    clearFrame(`<div class="expert-empty">${r.status === 'running' ? 'Generating…' : 'Queued…'}</div>`);
  }

  updateToggleButtons(!!displayUrl);

  const path = r.fileName;
  const chips = r.selections
    .map((s) => {
      const label = s.optionLabel ?? '';
      const tip = `${s.questionTitle}: ${label}`;
      return `<span class="gen-selection-chip c${escapeHtml(String(s.colorIndex))}" title="${escapeHtml(tip)}">${escapeHtml(label)}</span>`;
    })
    .join('');
  refs.meta.innerHTML = `
    <div class="expert-meta-path">${escapeHtml(path)}</div>
    <div class="expert-meta-chips">${chips}</div>
    <div class="expert-meta-status status-${r.status}">Status: <strong>${r.status}</strong>${
      r.errorMessage ? ` — ${escapeHtml(r.errorMessage)}` : ''
    }</div>
  `;

  const awaitingArea = isAwaitingAreaResult(r);
  const canReview = r.status === 'pending' || r.status === 'approved' || r.status === 'rejected' || r.status === 'error';
  const canReject = canReview || r.status === 'running';
  if (awaitingArea) {
    refs.approveBtn.disabled = false;
    refs.rejectBtn.disabled = false;
    refs.approveBtn.textContent = `Approve area ${(r.currentAreaIdx ?? 0) + 1}/${r.areasTotal} (A)`;
    refs.rejectBtn.textContent = `Regenerate area (R)`;
  } else {
    refs.approveBtn.disabled = !canReview || r.status === 'error';
    refs.rejectBtn.disabled = !canReject;
    refs.approveBtn.textContent = 'Approve (A)';
    refs.rejectBtn.textContent = 'Reject (R)';
  }
  refs.unreviewBtn.disabled = !(r.status === 'approved' || r.status === 'rejected');
}

type Snapshot = NonNullable<ExpertSession['snapshot']>;

function pickAreaForOutline(r: ExpertResult, snap: Snapshot | null): Area | null {
  if (!snap) return null;
  if (isAwaitingAreaResult(r) && r.currentAreaIdx !== null) {
    return snap.areas[r.currentAreaIdx] ?? null;
  }
  if (r.status === 'running' && r.currentAreaIdx !== null) {
    return snap.areas[r.currentAreaIdx] ?? null;
  }
  return null;
}

function positionAreaBox(
  frame: HTMLElement,
  box: HTMLElement,
  area: Area,
  baseW: number,
  baseH: number,
): void {
  const rect = frame.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return;
  const containerRatio = rect.width / rect.height;
  const imageRatio = baseW / baseH;
  let renderedW: number;
  let renderedH: number;
  let offsetX: number;
  let offsetY: number;
  if (imageRatio > containerRatio) {
    renderedW = rect.width;
    renderedH = rect.width / imageRatio;
    offsetX = 0;
    offsetY = (rect.height - renderedH) / 2;
  } else {
    renderedH = rect.height;
    renderedW = rect.height * imageRatio;
    offsetX = (rect.width - renderedW) / 2;
    offsetY = 0;
  }
  const { w, h } = areaDims(area);
  box.style.left = `${offsetX + (area.x / baseW) * renderedW}px`;
  box.style.top = `${offsetY + (area.y / baseH) * renderedH}px`;
  box.style.width = `${(w / baseW) * renderedW}px`;
  box.style.height = `${(h / baseH) * renderedH}px`;
}

function setupStageObservers(frame: HTMLElement): void {
  teardownStageObservers();
  stageResizeObs = new ResizeObserver(() => {
    repositionBoxIfAny();
  });
  stageResizeObs.observe(frame);
  stageWindowListener = () => repositionBoxIfAny();
  window.addEventListener('resize', stageWindowListener);
}

function teardownStageObservers(): void {
  if (stageResizeObs) {
    stageResizeObs.disconnect();
    stageResizeObs = null;
  }
  if (stageWindowListener) {
    window.removeEventListener('resize', stageWindowListener);
    stageWindowListener = null;
  }
}

function repositionBoxIfAny(): void {
  if (!refs) return;
  const frame = refs.stage.querySelector<HTMLElement>('.expert-stage-frame');
  if (!frame) return;
  const box = frame.querySelector<HTMLElement>('.expert-area-box');
  if (!box || box.classList.contains('hidden')) return;
  const r = expertSession.results.find((x) => x.id === selectedResultId);
  const snap = expertSession.snapshot;
  if (!r || !snap) return;
  const area = pickAreaForOutline(r, snap);
  if (!area) return;
  positionAreaBox(frame, box, area, snap.baseImage.width, snap.baseImage.height);
}

function updateToggleButtons(hasImage: boolean): void {
  if (!refs) return;
  refs.outlineToggleBtn.classList.toggle('is-off', outlineHidden);
  refs.baseToggleBtn.classList.toggle('is-active', showingBase);
  refs.outlineToggleBtn.disabled = !hasImage;
  refs.baseToggleBtn.disabled = !hasImage || !expertSession.snapshot?.baseImage.dataUrl;
}

function advance(dir: 1 | -1, skipReviewed: boolean): void {
  const results = expertSession.results;
  if (results.length === 0) return;
  const current = selectedResultId
    ? results.findIndex((r) => r.id === selectedResultId)
    : -1;
  const start = current < 0 ? 0 : current;
  let i = start;
  for (let step = 0; step < results.length; step++) {
    i = (i + dir + results.length) % results.length;
    const r = results[i];
    if (!skipReviewed) {
      selectedResultId = r.id;
      render();
      return;
    }
    if (r.status === 'pending' || r.status === 'error') {
      selectedResultId = r.id;
      render();
      return;
    }
  }
  // No candidates left — just render in place.
  render();
}

function onKeyDown(e: KeyboardEvent): void {
  if (!refs || refs.overlay.classList.contains('hidden')) return;
  if (view !== 'review') {
    if (e.key === 'Escape') {
      e.preventDefault();
      closeModal();
    }
    return;
  }
  // Ignore if user is typing in an input
  const t = e.target as HTMLElement | null;
  if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return;

  switch (e.key) {
    case 'a':
    case 'A':
    case ' ': {
      e.preventDefault();
      if (!selectedResultId) break;
      if (isAwaitingArea(selectedResultId)) {
        approveCurrentArea(selectedResultId);
        jumpToNextAwaiting(selectedResultId);
      } else {
        setResultStatus(selectedResultId, 'approved');
        advance(1, true);
      }
      break;
    }
    case 'r':
    case 'R':
    case 'Backspace': {
      e.preventDefault();
      if (!selectedResultId) break;
      if (isAwaitingArea(selectedResultId)) {
        regenerateCurrentArea(selectedResultId);
        jumpToNextAwaiting(selectedResultId);
      } else {
        setResultStatus(selectedResultId, 'rejected');
        advance(1, true);
      }
      break;
    }
    case 'u':
    case 'U': {
      e.preventDefault();
      if (selectedResultId) {
        setResultStatus(selectedResultId, 'pending');
        render();
      }
      break;
    }
    case 'o':
    case 'O': {
      e.preventDefault();
      outlineHidden = !outlineHidden;
      renderViewer();
      break;
    }
    case 'b':
    case 'B': {
      e.preventDefault();
      showingBase = !showingBase;
      renderViewer();
      break;
    }
    case 'ArrowRight':
      e.preventDefault();
      advance(1, false);
      break;
    case 'ArrowLeft':
      e.preventDefault();
      advance(-1, false);
      break;
    case 'Escape':
      e.preventDefault();
      closeModal();
      break;
  }
}
